import type {ComponentType} from 'react';
import React, {useState, useEffect, useRef} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import 'svg.draggable.js';

import type {DropzoneFile} from '@deviceinsight/ng-ui-components';
import {ImageUpload, MultiMenu, MultiMenuItem, outsideClick} from '@deviceinsight/ng-ui-components';
import {Button, Icon} from '@deviceinsight/ng-ui-basic-components';
import type {AssetWithMetaData, StateAccess} from '../types';
import AssetSearch from './assetselection/AssetSearch';
import {transformBlobUrlToSvgString, loadGroupDevices, getAssetGroupFromContext} from '../utils/FloorplanWidgetUtils';
import {MAX_SIZE, SVG_MIME_TYPE} from '../utils/consts';
import {paginationSettingsAll} from '../../../../api/searchAssets';
import {reLinkFloorplanSvg, removeFloorplanImage} from './FloorplanWidgetConfigFloorplanUtils';
import {
	onAddAssets,
	onClickAnyWhere,
	removeAllDevicesFromFloorplan,
	removeDeviceFromFloorplan,
	updateAssetScaling
} from './FloorplanWidgetDeviceUtils';
import SelectedDeviceInfo from './SelectedDeviceInfo';
import useOnClickOutside from '../../../common/useOnClickOutside';
import './FloorplanWidgetConfig.less';
import iconZoomOut from '../../../../assets/icon-zoom-out.png';
import iconZoomIn from '../../../../assets/icon-zoom-in.png';
import iconMenu from '../../../../assets/icon-menu-horizontal.png';
import iconUpload from '../../../../assets/icon-upload.png';
import SvgUploadError from './SvgUploadError';
import {WidgetSvg} from '../hooks/types';
import {WidgetConfigProps} from '@deviceinsight/ng-ui-scale-lib/api';

const FloorplanWidgetConfig = (props: WidgetConfigProps) => {
	const {
		config,
		config: {
			settings,
			settings: {floorplanSvg, uniqueSvgKey, headline}
		},
		context,
		onChangeConfig
	} = props;
	const [availableDevices, setAvailableDevices] = useState<AssetWithMetaData[]>([]);
	const [svgRootObj, setSvgRootObj] = useState<WidgetSvg>();
	const [isSearching, setIsSearching] = useState(false);
	const [rejectedFile, setRejectedFile] = useState<DropzoneFile | null | undefined>();
	// handler for popup
	const [selectedDevice, setSelectedDevice] = useState<AssetWithMetaData>();
	const stateAccessParams: StateAccess = {
		availableDevices,
		setAvailableDevices,
		svgRootObj,
		setSvgRootObj,
		selectedDevice,
		setSelectedDevice,
		isSearching,
		setIsSearching
	};

	const updateSvg = () => {
		const container = document.getElementById('floorplanContainer');

		if (container) {
			const currentSvg = container.innerHTML;
			onChangeConfig({...config, settings: {...settings, floorplanSvg: currentSvg}});
		}
	};

	const assetGroup = getAssetGroupFromContext(context);
	useEffect(() => {
		reLinkFloorplanSvg(floorplanSvg, updateSvg, uniqueSvgKey, assetGroup, stateAccessParams);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uniqueSvgKey]);
	const ref = useRef(null);
	useOnClickOutside(ref, (event) => onClickAnyWhere(event, setSelectedDevice, svgRootObj));
	return (
		<div
			className="svg-edit-container di form"
			onClick={(event) => onClickAnyWhere(event as unknown as MouseEvent, setSelectedDevice, svgRootObj)}
			ref={ref}>
			<div className="fields boxheadline-vertical">
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.floorplan.headline">Headline</Trans>
					</label>
					<input
						type="text"
						value={headline}
						onChange={(e) => onChangeConfig({...config, settings: {...settings, headline: e.target.value}})}
					/>
				</div>
				<div className="field">
					{floorplanSvg && (
						<Button naked onClick={() => setIsSearching(true)}>
							<Icon add />
							<Trans i18nKey="dashboard.widgets.floorplan.placeassets">Place Assets</Trans>
						</Button>
					)}
				</div>
				<div className="field separator">
					{floorplanSvg && (
						<Button
							naked
							onClick={() =>
								loadGroupDevices('', assetGroup, availableDevices, paginationSettingsAll).then(
									({content: assetsAndGroups}) => {
										onAddAssets(assetsAndGroups, updateSvg, stateAccessParams);
									}
								)
							}
							disabled={!floorplanSvg}>
							<Icon add />
							<Trans i18nKey="dashboard.widgets.floorplan.placeallassets">Place all</Trans>
						</Button>
					)}
				</div>
				{floorplanSvg && availableDevices.length > 0 && (
					<div className="field  separator">
						<Button naked onClick={() => updateAssetScaling(svgRootObj, updateSvg, false)}>
							<img src={iconZoomOut} className="di icon" alt="smaller" />
							<Trans i18nKey="dashboard.widgets.floorplan.smaller">Smaller</Trans>
						</Button>
						<Button naked onClick={() => updateAssetScaling(svgRootObj, updateSvg, true)}>
							<img src={iconZoomIn} className="di icon" alt="larger" />
							<Trans i18nKey="dashboard.widgets.floorplan.larger">Larger</Trans>
						</Button>
					</div>
				)}
				{floorplanSvg && (
					<div>
						<MultiMenu
							menuButton={
								<Button naked className="di button" disabled={!floorplanSvg}>
									<img src={iconMenu} alt="Update" />
								</Button>
							}>
							<MultiMenuItem
								icon={<Icon del />}
								onClick={() => removeAllDevicesFromFloorplan(updateSvg, stateAccessParams)}>
								<Trans i18nKey="dashboard.widgets.floorplan.removeallassets">
									Remove all assets from floorplan
								</Trans>
							</MultiMenuItem>
							<MultiMenuItem
								icon={<img src={iconUpload} className="di icon" alt="upload" />}
								onClick={() => {
									removeFloorplanImage(config, onChangeConfig, stateAccessParams);
									onChangeConfig({
										...config,
										settings: {...settings, floorplanSvg: undefined, uniqueSvgKey: ''}
									});
								}}>
								<Trans i18nKey="dashboard.widgets.floorplan.uploadfloorplan">
									Upload new floorplan
								</Trans>
							</MultiMenuItem>
						</MultiMenu>
					</div>
				)}
			</div>
			<div>
				{floorplanSvg ? (
					<div id="floorplanContainer" className="floorplan-svg-container" />
				) : (
					<div>
						<ImageUpload
							imageUrl={floorplanSvg}
							accept={SVG_MIME_TYPE}
							onRemoveFile={() => removeFloorplanImage(config, onChangeConfig, stateAccessParams)}
							onSelectFile={(file: DropzoneFile) => {
								setRejectedFile(undefined);
								const {preview} = file;

								if (preview) {
									transformBlobUrlToSvgString(preview).then((svgContent) => {
										onChangeConfig({
											...config,
											settings: {...settings, floorplanSvg: svgContent, uniqueSvgKey: preview}
										});
									});
								}
							}}
							onDropRejected={(files: DropzoneFile[]) => setRejectedFile(files[0])}
							maxSize={MAX_SIZE}
						/>
						<SvgUploadError rejectedFile={rejectedFile} />
					</div>
				)}
			</div>
			<SelectedDeviceInfo
				selectedDevice={selectedDevice}
				onRemoveClicked={() => removeDeviceFromFloorplan(updateSvg, stateAccessParams)}
			/>
			{isSearching && floorplanSvg && (
				<div className="floorplan-asset-search">
					<AssetSearch
						addedDevices={availableDevices}
						assetGroup={assetGroup}
						onCancelSearch={() => setIsSearching(false)}
						onAddAssets={(assetsWithGroups) => onAddAssets(assetsWithGroups, updateSvg, stateAccessParams)}
					/>
				</div>
			)}
		</div>
	);
};

export default outsideClick(FloorplanWidgetConfig) as ComponentType<WidgetConfigProps>;
